/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { MutableRefObject } from 'react';

import { Grid, Typography2, breakpoints } from '@coursera/cds-core';
import {
  AIGenerateIcon,
  AnswerCorrectIcon,
  ArrowUpIcon,
  CertificateIcon,
  DegreeIcon,
  FolderIcon,
  GlobeIcon,
  HomeIcon,
  HonorsAssignmentIcon,
  InstitutionIcon,
  ProfileIcon,
  ReadingIcon,
  StarIcon,
} from '@coursera/cds-icons';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import DomainIntroMedia from 'bundles/premium-hub/components/degrees/redesign/DomainIntroMedia';
import Container from 'bundles/premium-hub/components/shared/Container';
import { useModuleSectionEventTrackingData } from 'bundles/premium-hub/hooks/useModuleSectionEventTrackingData';

export type BenefitItem = {
  cdsIconName: string;
  title: string;
  description: string;
};

export type MediaAsset = {
  url: string;
  description?: string | null;
  contentType?: string | null;
};

export type DomainIntroModuleProps = {
  heading: string;
  benefits: BenefitItem[];
  media?: MediaAsset;
};

// The possible input values are controlled by a select field in Contentful and is controlled/maintained by engineering
const VALID_ICONS = {
  AIGenerateIcon,
  AnswerCorrectIcon,
  ArrowUpIcon,
  CertificateIcon,
  DegreeIcon,
  FolderIcon,
  GlobeIcon,
  HomeIcon,
  HonorsAssignmentIcon,
  InstitutionIcon,
  ProfileIcon,
  ReadingIcon,
  StarIcon,
};

const styles = {
  moduleContainer: css`
    margin-top: var(--cds-spacing-400);

    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-300);
    }
  `,
  panel: css`
    background: var(--cds-color-emphasis-primary-background-xweak);
    border-radius: var(--cds-border-radius-100);
    padding: var(--cds-spacing-400);

    ${breakpoints.down('sm')} {
      margin: 0 -12px;
      padding: var(--cds-spacing-400);
      border-radius: 0;
      width: calc(100% + 24px);
    }
  `,
  heading: css`
    margin-bottom: var(--cds-spacing-400);
  `,
  benefitItem: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);

    ${breakpoints.down('xs')} {
      flex-direction: row;
      gap: var(--cds-spacing-200);
    }
  `,
  benefitTitle: css`
    margin-bottom: var(--cds-spacing-50);
  `,
  mediaContainer: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
  `,
};

const DomainIntroModule = ({ heading, benefits, media }: DomainIntroModuleProps) => {
  const { index: sectionIndex } = useModuleSectionEventTrackingData();
  const visibilityTrackingRef: MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_page_section', {
    pageSection: { sectionName: 'domain_introduction', index: sectionIndex },
  });

  if (!benefits.length) {
    return null;
  }

  return (
    <Container css={styles.moduleContainer}>
      <div ref={visibilityTrackingRef} data-testid="domain-intro-module">
        <div css={styles.panel}>
          <Grid container spacing={24}>
            <Grid item xs={12} md={6}>
              <Typography2 component="h2" variant="subtitleLarge" css={styles.heading}>
                {heading}
              </Typography2>
              <Grid container spacing={24}>
                {benefits.map((benefit) => {
                  const Icon = VALID_ICONS[benefit.cdsIconName as keyof typeof VALID_ICONS];
                  return (
                    <Grid key={benefit.title} item xs={12} sm={6}>
                      <div css={styles.benefitItem}>
                        {Icon && <Icon size="large" />}
                        <div>
                          <Typography2 component="h3" variant="subtitleMedium" css={styles.benefitTitle}>
                            {benefit.title}
                          </Typography2>
                          <Typography2 component="p" variant="bodyPrimary">
                            {benefit.description}
                          </Typography2>
                        </div>
                      </div>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
            <Grid item xs={12} md={6} css={styles.mediaContainer}>
              <DomainIntroMedia heading={heading} media={media} />
            </Grid>
          </Grid>
        </div>
      </div>
    </Container>
  );
};

export default DomainIntroModule;
